import {
  useCallback,
  useMemo,
  useState,
  ReactNode,
  memo,
  FC,
  useEffect,
  useRef,
  Fragment,
} from 'react';

import Fuse from 'fuse.js';
import { SearchIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * A single menu item used within SearchableDropdownMenu.
 * Supports nested children, disabled state, optional icon, and custom search keywords.
 */
export interface SearchableMenuItem {
  id: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  searchText?: string;
  children?: SearchableMenuItem[];
  /**
   * When true, the item is hidden in normal rendering but still searchable.
   */
  searchOnly?: boolean;
  section?: string;
}

/**
 * A group of menu items with an optional label and separator.
 */
export interface SearchableMenuGroup {
  id: string;
  label?: string;
  items: SearchableMenuItem[];
  /**
   * When true, a separator is rendered before this group (except for the first one).
   */
  separator?: boolean;
}

/**
 * Props for the SearchableDropdownMenu component.
 */
export interface SearchableDropdownMenuProps {
  /**
   * The element that triggers opening of the dropdown.
   */
  trigger: ReactNode;
  /**
   * Menu groups to render inside the dropdown.
   */
  groups: SearchableMenuGroup[];
  /**
   * Placeholder text for the search input.
   */
  searchPlaceholder?: string;
  /**
   * Message displayed when search yields no results.
   */
  emptyMessage?: string;
  emptyState?: ReactNode;
  /**
   * Additional className for the content (e.g., width).
   */
  className?: string;
  /**
   * Alignment of the dropdown relative to its trigger.
   */
  align?: 'start' | 'center' | 'end';
  /**
   * Max height utility (e.g., max-h-80). Content scrolls inside this height.
   */
  maxHeight?: string;
  /**
   * Controlled open state. If provided, component behaves as controlled.
   */
  isOpen?: boolean;
  /**
   * Callback invoked when the open state changes.
   */
  onOpenChange?: (open: boolean) => void;
  onCloseAutoFocus?: (event: Event) => void;
}

interface SearchableContentProps {
  groups: SearchableMenuGroup[];
  searchPlaceholder?: string;
  emptyMessage?: string;
  emptyState?: ReactNode;
  maxHeight?: string;
  children?: ReactNode;
  onClose?: () => void;
}

interface SearchableMenuItemWithParent extends SearchableMenuItem {
  parentLabel?: string;
  parentPath?: string[];
  groupLabel?: string;
}

/**
 * Special key for ungrouped items in search results bucketing.
 */
const UNGROUPED_BUCKET = '__ungrouped__';

/**
 * Flatten a hierarchical set of menu items to a simple array, preserving parent path context
 * to enable contextual rendering in search results.
 *
 * @param items - Hierarchical menu items
 * @param groupLabel - Optional label for the containing group
 * @returns Flat array of items with parent and group context
 */
function flattenItems(
  items: SearchableMenuItem[],
  groupLabel?: string
): SearchableMenuItemWithParent[] {
  const flattened: SearchableMenuItemWithParent[] = [];

  function walk(nodes: SearchableMenuItem[], parentPath: string[] = []): void {
    for (const node of nodes) {
      const base: SearchableMenuItemWithParent = {
        ...node,
        searchText: node.searchText ?? node.label,
        groupLabel,
        parentPath: parentPath.length ? [...parentPath] : undefined,
        parentLabel: parentPath.length ? parentPath[parentPath.length - 1] : undefined,
      };
      flattened.push(base);

      if (node.children?.length) {
        walk(node.children, [...parentPath, node.label]);
      }
    }
  }

  walk(items);
  return flattened;
}

/**
 * Collect leaf descendants for a parent item so that matched parent categories expand to all leaves
 * in search results. Descendants retain their parentPath/groupLabel context.
 *
 * @param parent - The matched parent item with children
 * @param flattenedById - A map of flattened items by id
 */
function collectLeafDescendants(
  parent: SearchableMenuItemWithParent,
  flattenedById: Map<string, SearchableMenuItemWithParent>
): SearchableMenuItemWithParent[] {
  const leaves: SearchableMenuItemWithParent[] = [];

  const walk = (node: SearchableMenuItem): void => {
    if (node.children && node.children.length > 0) {
      node.children.forEach(walk);
    } else {
      const flat = flattenedById.get(node.id);
      if (flat) {
        leaves.push(flat);
      }
    }
  };

  if (parent.children && parent.children.length > 0) {
    parent.children.forEach(walk);
  }

  return leaves;
}

/**
 * Build a map from item id to its index in the "allVisibleItems" array for O(1) lookups.
 */
function buildIdToIndex(items: SearchableMenuItem[]): Map<string, number> {
  const map = new Map<string, number>();
  items.forEach((it, idx) => {
    map.set(it.id, idx);
  });
  return map;
}

/**
 * Group items by their top-level parent label for search result display.
 * Ungrouped items (no parent) are kept in a special first bucket.
 */
function groupByTopParent(
  items: SearchableMenuItemWithParent[]
): { key: string; label: string; items: SearchableMenuItemWithParent[] }[] {
  const buckets = new Map<string, { label: string; items: SearchableMenuItemWithParent[] }>();
  const order: string[] = [];

  for (const it of items) {
    const topParent = it.parentPath?.[0];
    const key = topParent ?? UNGROUPED_BUCKET;

    if (!buckets.has(key)) {
      buckets.set(key, { label: topParent ?? '', items: [] });
      order.push(key);
    }
    buckets.get(key)!.items.push(it);
  }

  // Ensure ungrouped bucket appears first
  order.sort((a, b) => {
    if (a === UNGROUPED_BUCKET) {
      return -1;
    }
    if (b === UNGROUPED_BUCKET) {
      return 1;
    }
    return 0;
  });

  return order.map((key) => ({ key, ...buckets.get(key)! }));
}

function groupBySection(
  items: SearchableMenuItem[]
): { key: string; label?: string; items: SearchableMenuItem[] }[] {
  const sections: { key: string; label?: string; items: SearchableMenuItem[] }[] = [];

  for (const item of items) {
    const current = sections[sections.length - 1];

    if (current && current.label === item.section) {
      current.items.push(item);
    } else {
      sections.push({ key: item.id, label: item.section, items: [item] });
    }
  }

  return sections;
}

interface RenderMenuItemProps {
  item: SearchableMenuItem | SearchableMenuItemWithParent;
  maxHeight?: string;
  isSelected: boolean;
  onHover: () => void;
  openSubmenus: Record<string, boolean>;
  setOpenSubmenus: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  allVisibleItems: SearchableMenuItem[];
  idToIndex: Map<string, number>;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  isSearching: boolean;
  suppressContextLabel?: boolean;
  itemClassName?: string;
  labelClassName?: string;
}

/**
 * Renders a single menu item with optional submenu and/or context information during search.
 * Uses a props object to reduce positional argument noise and improve readability.
 */
function renderMenuItem({
  item,
  maxHeight,
  isSelected,
  onHover,
  openSubmenus,
  setOpenSubmenus,
  allVisibleItems,
  idToIndex,
  selectedIndex,
  setSelectedIndex,
  isSearching,
  suppressContextLabel,
  itemClassName,
  labelClassName,
}: RenderMenuItemProps): ReactNode {
  if (item.children && item.children.length > 0) {
    // During normal view, only render as submenu if not all children are searchOnly
    const allChildrenAreSearchOnly =
      !isSearching && item.children.every((child) => child.searchOnly);
    const visibleChildren = item.children.filter((child) => !child.searchOnly || isSearching);
    const hasSections = visibleChildren.some((child) => !!child.section);

    if (allChildrenAreSearchOnly) {
      // All children are searchOnly, render as regular clickable item (no submenu)
      return (
        <DropdownMenuItem
          key={item.id}
          onClick={() => {
            try {
              item.onClick?.();
            } catch (error) {
              console.error('Error in onClick handler:', error);
            }
          }}
          disabled={item.disabled}
          className={cn(
            'flex items-center gap-2 text-foreground',
            itemClassName,
            isSelected && 'bg-highlight-background'
          )}
          onMouseEnter={onHover}
          onMouseLeave={() => setSelectedIndex(-1)}
        >
          {item.icon}
          <span className="text-foreground">{item.label}</span>
        </DropdownMenuItem>
      );
    }

    return (
      <DropdownMenuSub
        key={item.id}
        open={openSubmenus[item.id] || false}
        onOpenChange={(open) => {
          setOpenSubmenus((prev) => ({ ...prev, [item.id]: open }));
          setSelectedIndex(-1);
        }}
      >
        <DropdownMenuSubTrigger
          className={cn(
            'flex items-center gap-2 text-foreground',
            itemClassName,
            isSelected && 'bg-highlight-background'
          )}
          onMouseEnter={onHover}
          onMouseLeave={() => setSelectedIndex(-1)}
          data-slot="dropdown-menu-sub-trigger"
        >
          {item.icon}
          <span className="text-foreground">{item.label}</span>
        </DropdownMenuSubTrigger>
        <DropdownMenuPortal>
          <DropdownMenuSubContent
            className={cn(
              maxHeight,
              'overflow-y-auto',
              hasSections && 'border-stroke p-0 shadow-md'
            )}
            sideOffset={4}
          >
            {groupBySection(visibleChildren).map((section, sectionIndex) => {
              const renderedItems = section.items.map((child) => {
                const childIndex = idToIndex.get(child.id) ?? -1;

                return renderMenuItem({
                  item: child,
                  maxHeight,
                  isSelected: childIndex === selectedIndex,
                  onHover: () => {
                    if (childIndex >= 0) {
                      setSelectedIndex(childIndex);
                    }
                  },
                  openSubmenus,
                  setOpenSubmenus,
                  allVisibleItems,
                  idToIndex,
                  selectedIndex,
                  setSelectedIndex,
                  isSearching,
                  suppressContextLabel,
                  itemClassName: section.label
                    ? cn(itemClassName, 'h-8 min-w-32 bg-surface-popover px-2 py-1.5')
                    : itemClassName,
                  labelClassName: section.label
                    ? 'line-clamp-1 overflow-hidden text-sm leading-5 font-normal text-ellipsis'
                    : labelClassName,
                });
              });

              if (!section.label) {
                return <Fragment key={section.key}>{renderedItems}</Fragment>;
              }

              return (
                <Fragment key={section.key}>
                  {sectionIndex > 0 && <DropdownMenuSeparator />}
                  <div className="flex flex-col items-stretch gap-px bg-surface-popover p-1">
                    <div className="flex h-8 min-w-32 items-center gap-2 rounded-lg bg-surface-popover px-2 py-1">
                      <span className="line-clamp-1 flex-1 overflow-hidden text-xs leading-4 font-normal text-ellipsis text-foreground-muted">
                        {section.label}
                      </span>
                    </div>
                    {renderedItems}
                  </div>
                </Fragment>
              );
            })}
          </DropdownMenuSubContent>
        </DropdownMenuPortal>
      </DropdownMenuSub>
    );
  }

  const itemWithParent = item as SearchableMenuItemWithParent;

  // Determine the context label to show during search
  const contextFromPath =
    itemWithParent.parentPath && itemWithParent.parentPath.length > 0
      ? itemWithParent.parentPath.join(' › ')
      : undefined;
  const contextLabel =
    isSearching && !suppressContextLabel
      ? contextFromPath || itemWithParent.groupLabel || itemWithParent.parentLabel
      : undefined;

  return (
    <DropdownMenuItem
      key={item.id}
      onClick={() => {
        try {
          item.onClick?.();
        } catch (error) {
          console.error('Error in onClick handler:', error);
        }
      }}
      disabled={item.disabled}
      className={cn(
        'flex items-center gap-2 text-foreground',
        itemClassName,
        isSelected && 'bg-highlight-background'
      )}
      onMouseEnter={onHover}
      onMouseLeave={() => setSelectedIndex(-1)}
    >
      {item.icon}
      <span className={cn('flex min-w-0 flex-col', labelClassName && 'flex-1')}>
        {contextLabel && <span className="text-xs text-foreground-muted">{contextLabel}</span>}
        <span className={cn('wrap-break-word text-foreground', labelClassName)}>{item.label}</span>
      </span>
    </DropdownMenuItem>
  );
}

/**
 * SearchableContent
 *
 * Encapsulates the search, filtering, grouping and keyboard navigation logic.
 * Isolated from the parent to avoid unnecessary re-renders during search.
 */
const SearchableContent: FC<SearchableContentProps> = memo(
  ({ groups, searchPlaceholder, emptyMessage, emptyState, maxHeight = 'max-h-80', onClose }) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIndex, setSelectedIndex] = useState<number>(-1);
    const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({});
    const inputRef = useRef<HTMLInputElement>(null);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    }, []);

    const handleSearchClick = useCallback((e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    }, []);

    const fuseInstances = useMemo(() => {
      return groups.map((group) => {
        const allItems = flattenItems(group.items, group.label);
        return new Fuse(allItems, {
          keys: ['searchText', 'label', 'parentLabel', 'groupLabel', 'parentPath'],
          threshold: 0.3,
          ignoreLocation: true,
          includeScore: true,
          shouldSort: true,
        });
      });
    }, [groups]);

    const filteredGroups = useMemo(() => {
      if (!searchTerm.trim()) {
        return groups;
      }

      return groups
        .map((group, index) => {
          // Run fuzzy search on flattened items
          const searchResults = fuseInstances[index].search(searchTerm);

          // Recreate flattened items to retain parentPath/groupLabel context
          const flattenedAll = flattenItems(group.items, group.label);
          const flattenedById = new Map<string, SearchableMenuItemWithParent>(
            flattenedAll.map((fi) => [fi.id, fi])
          );

          // Build final set of items:
          // - Expand matched parents (items with children) to all their leaf descendants
          // - Exclude any items that have children (i.e., parent categories)
          // - Deduplicate while preserving order
          const included = new Map<string, SearchableMenuItemWithParent>();

          for (const result of searchResults) {
            const candidate = result.item as SearchableMenuItemWithParent;

            if (candidate.children && candidate.children.length > 0) {
              const leaves = collectLeafDescendants(candidate, flattenedById);
              for (const leaf of leaves) {
                if (!included.has(leaf.id)) {
                  included.set(leaf.id, leaf);
                }
              }
            } else {
              if (!included.has(candidate.id)) {
                included.set(candidate.id, candidate);
              }
            }
          }

          const filteredItems = Array.from(included.values()).filter(
            (it) => !(it.children && it.children.length > 0)
          );

          return { ...group, items: filteredItems };
        })
        .filter((group) => group.items.length > 0);
    }, [groups, searchTerm, fuseInstances]);

    // Collect all visible items based on current filtered groups and open submenus
    const allVisibleItems = useMemo(() => {
      const visibleItems: SearchableMenuItem[] = [];

      const collectVisibleItems = (items: SearchableMenuItem[], isInSubmenu = false) => {
        items.forEach((item) => {
          // Skip searchOnly items unless we're in a search context or in a submenu
          if (item.searchOnly && !searchTerm.trim() && !isInSubmenu) {
            return;
          }

          if (!item.disabled) {
            visibleItems.push(item);
            // Include children if submenu is open
            if (item.children && item.children.length > 0 && openSubmenus[item.id]) {
              collectVisibleItems(item.children, true);
            }
          }
        });
      };

      filteredGroups.forEach((group) => {
        collectVisibleItems(group.items);
      });

      return visibleItems;
    }, [filteredGroups, openSubmenus, searchTerm]);

    const idToIndex = useMemo(() => buildIdToIndex(allVisibleItems), [allVisibleItems]);

    // Collect only top-level items for Tab navigation
    const topLevelItems = useMemo(() => {
      const items: SearchableMenuItem[] = [];

      filteredGroups.forEach((group) => {
        group.items.forEach((item) => {
          if (!item.disabled) {
            items.push(item);
          }
        });
      });

      return items;
    }, [filteredGroups]);

    // Reset selected index when filtered results change
    useEffect(() => {
      setSelectedIndex(-1);
    }, [filteredGroups]);

    // Adjust selected index when visible items change due to submenu state changes
    useEffect(() => {
      if (selectedIndex >= allVisibleItems.length) {
        setSelectedIndex(allVisibleItems.length - 1);
      }
    }, [allVisibleItems.length, selectedIndex]);

    // Handle keyboard navigation - need to be after filteredGroups is defined
    const handleKeyNavigation = useCallback(
      (e: React.KeyboardEvent) => {
        e.stopPropagation();

        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setSelectedIndex((prev) => {
              const nextIndex = prev + 1;
              return nextIndex >= allVisibleItems.length ? 0 : nextIndex;
            });
            break;
          case 'ArrowUp':
            e.preventDefault();
            setSelectedIndex((prev) => {
              const nextIndex = prev - 1;
              return nextIndex < 0 ? allVisibleItems.length - 1 : nextIndex;
            });
            break;
          case 'Enter':
            e.preventDefault();
            if (selectedIndex >= 0 && selectedIndex < allVisibleItems.length) {
              const selectedItem = allVisibleItems[selectedIndex];
              if (selectedItem.children && selectedItem.children.length > 0) {
                // If item has both children and onClick, call onClick
                if (selectedItem.onClick && !selectedItem.disabled) {
                  try {
                    selectedItem.onClick();
                    // Close the dropdown after clicking an item with keyboard
                    onClose?.();
                  } catch (error) {
                    console.error('Error in onClick handler:', error);
                  }
                } else {
                  // Otherwise just toggle submenu open state
                  setOpenSubmenus((prev) => ({
                    ...prev,
                    [selectedItem.id]: !prev[selectedItem.id],
                  }));
                }
              } else if (selectedItem.onClick && !selectedItem.disabled) {
                try {
                  selectedItem.onClick();
                  // Close the dropdown after clicking an item with keyboard
                  onClose?.();
                } catch (error) {
                  console.error('Error in onClick handler:', error);
                }
              }
            }
            break;
          case 'ArrowRight':
            e.preventDefault();
            if (selectedIndex >= 0 && selectedIndex < allVisibleItems.length) {
              const selectedItem = allVisibleItems[selectedIndex];
              if (selectedItem.children && selectedItem.children.length > 0) {
                setOpenSubmenus((prev) => ({ ...prev, [selectedItem.id]: true }));
              }
            }
            break;
          case 'ArrowLeft':
            e.preventDefault();
            if (selectedIndex >= 0 && selectedIndex < allVisibleItems.length) {
              const selectedItem = allVisibleItems[selectedIndex];
              if (selectedItem.children && selectedItem.children.length > 0) {
                setOpenSubmenus((prev) => ({ ...prev, [selectedItem.id]: false }));
              }
            }
            break;
          case 'Tab':
            e.preventDefault();
            if (topLevelItems.length > 0) {
              // Tab navigates only through top-level items
              if (selectedIndex === -1) {
                // No selection, go to first top-level item
                const firstTopLevelItem = topLevelItems[0];
                const firstIndex = allVisibleItems.findIndex(
                  (item) => item.id === firstTopLevelItem.id
                );
                setSelectedIndex(firstIndex);
              } else {
                const currentItem = allVisibleItems[selectedIndex];

                // Find which top-level item we should go to next
                let nextTopLevelIndex = 0;

                // Check if current item is a top-level item
                const currentTopLevelIndex = topLevelItems.findIndex(
                  (item) => item.id === currentItem.id
                );

                if (currentTopLevelIndex !== -1) {
                  // Currently on a top-level item, go to next one
                  nextTopLevelIndex = (currentTopLevelIndex + 1) % topLevelItems.length;
                } else {
                  // Currently on a submenu item, find its parent top-level item
                  const findParentTopLevel = (item: SearchableMenuItem): number => {
                    for (let i = 0; i < topLevelItems.length; i++) {
                      const topItem = topLevelItems[i];
                      if (topItem.children) {
                        // Check direct children and nested children
                        const checkChildren = (children: SearchableMenuItem[]): boolean => {
                          return children.some((child) => {
                            if (child.id === item.id) {
                              return true;
                            }
                            if (child.children) {
                              return checkChildren(child.children);
                            }
                            return false;
                          });
                        };

                        if (checkChildren(topItem.children)) {
                          return i;
                        }
                      }
                    }
                    return -1;
                  };

                  const parentIndex = findParentTopLevel(currentItem);
                  if (parentIndex !== -1) {
                    nextTopLevelIndex = (parentIndex + 1) % topLevelItems.length;
                  }
                }

                const nextTopLevelItem = topLevelItems[nextTopLevelIndex];
                const nextIndex = allVisibleItems.findIndex(
                  (item) => item.id === nextTopLevelItem.id
                );
                setSelectedIndex(nextIndex);
              }
            }
            break;
          case 'Escape':
            // Let the dropdown handle escape
            break;
          default:
            // Reset selection when typing
            setSelectedIndex(-1);
        }
      },
      [allVisibleItems, selectedIndex, topLevelItems, onClose]
    );

    const renderGroup = (group: SearchableMenuGroup, groupIndex: number, isSearching: boolean) => {
      const showSeparator = group.separator && groupIndex > 0;

      // When searching, group items by their parent menu
      if (isSearching && group.items.length > 0) {
        const buckets = groupByTopParent(group.items as SearchableMenuItemWithParent[]);

        return (
          <div key={group.id || `group-${groupIndex}`}>
            {showSeparator && <DropdownMenuSeparator key={`${group.id}-separator-group`} />}
            {buckets.map((bucket, index) => {
              const isUngrouped = bucket.key === UNGROUPED_BUCKET;

              // Show separator before every bucket except the first one
              const showBucketSeparator = index > 0;

              return (
                <div key={bucket.key}>
                  {showBucketSeparator && (
                    <DropdownMenuSeparator key={`${bucket.key}-separator-bucket`} />
                  )}
                  {!isUngrouped && bucket.items.length > 0 && (
                    <div className="px-3 pt-1.5 pb-1 text-xs text-foreground-muted">
                      {bucket.label}
                    </div>
                  )}
                  {bucket.items.length > 0 && (
                    <DropdownMenuGroup>
                      {bucket.items.map((item) => {
                        const itemIndex = idToIndex.get(item.id) ?? -1;
                        const isSelected = itemIndex === selectedIndex;

                        return renderMenuItem({
                          item,
                          maxHeight,
                          isSelected,
                          onHover: () => {
                            if (itemIndex >= 0) {
                              setSelectedIndex(itemIndex);
                            }
                          },
                          openSubmenus,
                          setOpenSubmenus,
                          allVisibleItems,
                          idToIndex,
                          selectedIndex,
                          setSelectedIndex,
                          isSearching,
                          suppressContextLabel: !isUngrouped,
                          itemClassName: !isUngrouped ? 'pl-3' : undefined,
                        });
                      })}
                    </DropdownMenuGroup>
                  )}
                </div>
              );
            })}
          </div>
        );
      }

      // Not searching - render normally
      return (
        <div key={group.id || `group-${groupIndex}`}>
          {showSeparator && <DropdownMenuSeparator key={`${group.id}-separator-normal`} />}
          <DropdownMenuGroup>
            {group.items.map((item) => {
              const itemIndex = idToIndex.get(item.id) ?? -1;
              const isSelected = itemIndex === selectedIndex;

              return renderMenuItem({
                item,
                maxHeight,
                isSelected,
                onHover: () => {
                  if (itemIndex >= 0) {
                    setSelectedIndex(itemIndex);
                  }
                },
                openSubmenus,
                setOpenSubmenus,
                allVisibleItems,
                idToIndex,
                selectedIndex,
                setSelectedIndex,
                isSearching,
                suppressContextLabel: false,
                itemClassName: undefined,
              });
            })}
          </DropdownMenuGroup>
        </div>
      );
    };

    const hasSearchResults = filteredGroups.length > 0;
    const defaultEmptyMessage = emptyMessage || t('common.no_results');
    const defaultSearchPlaceholder = searchPlaceholder || t('common.search_dots');

    // Radix moves focus to items on hover - refocus input to allow typing (skip when submenus are open)
    const handleContentFocusCapture = useCallback(
      (e: React.FocusEvent) => {
        const target = e.target as HTMLElement;
        const hasOpenSubmenu = Object.values(openSubmenus).some(Boolean);
        if (target.closest('[data-slot="dropdown-menu-sub-trigger"]') || hasOpenSubmenu) {
          return;
        }
        requestAnimationFrame(() => {
          inputRef.current?.focus();
        });
      },
      [openSubmenus]
    );

    return (
      <>
        <div className="sticky top-0 z-10 -mx-1 bg-background px-1 pt-1">
          <div className="relative">
            <SearchIcon className="absolute top-1/2 left-2 h-4 w-4 -translate-y-1/2 text-foreground-muted" />
            <Input
              ref={inputRef}
              placeholder={defaultSearchPlaceholder}
              aria-label={defaultSearchPlaceholder}
              value={searchTerm}
              onChange={handleSearchChange}
              onClick={handleSearchClick}
              onKeyDown={handleKeyNavigation}
              className="h-8 border-none pl-8 focus-visible:ring-0 focus-visible:ring-offset-0"
              autoFocus
            />
          </div>
          <DropdownMenuSeparator />
        </div>

        <div onFocusCapture={handleContentFocusCapture}>
          {hasSearchResults
            ? filteredGroups.map((group, index) => renderGroup(group, index, !!searchTerm.trim()))
            : searchTerm.trim()
              ? (emptyState ?? (
                  <DropdownMenuItem disabled>
                    <span className="text-foreground-muted">{defaultEmptyMessage}</span>
                  </DropdownMenuItem>
                ))
              : null}
        </div>
      </>
    );
  }
);

SearchableContent.displayName = 'SearchableContent';

/**
 * SearchableDropdownMenu
 *
 * A generic, searchable dropdown built on Shadcn's DropdownMenu with nested submenus, fuzzy search,
 * and full keyboard navigation.
 *
 * @param trigger - ReactNode that opens the dropdown
 * @param groups - Array of grouped menu items; groups render in order, and may include submenus via children
 * @param searchPlaceholder - Optional placeholder for the search input
 * @param emptyMessage - Optional message to show when no results match the search
 * @param className - Optional width and sizing classes (e.g., "w-56"); the menu sizes to its
 *   content when omitted
 * @param align - Alignment of the menu relative to the trigger ("start" | "center" | "end")
 * @param maxHeight - Vertical size limit for the content area (e.g., "max-h-80"); content scrolls
 * @param onOpenChange - Called with dropdown visibility changes
 * @returns JSX.Element
 *
 * @example
 * <SearchableDropdownMenu
 *   trigger={<Button>Filters</Button>}
 *   groups={menuGroups}
 *   searchPlaceholder={t('brain_logs.searchFilters')}
 *   emptyMessage={t('brain_logs.no_filters_found')}
 *   className="w-56"
 *   maxHeight="max-h-100"
 * />
 */
export const SearchableDropdownMenu = memo(
  ({
    trigger,
    groups,
    searchPlaceholder,
    emptyMessage,
    emptyState,
    className,
    align = 'start',
    maxHeight = 'max-h-80',
    isOpen: propIsOpen,
    onOpenChange,
    onCloseAutoFocus,
  }: SearchableDropdownMenuProps) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = propIsOpen !== undefined;
    const isOpen = isControlled ? propIsOpen : internalOpen;

    const handleOpenChange = useCallback(
      (newOpen: boolean) => {
        if (!isControlled) {
          setInternalOpen(newOpen);
        }
        onOpenChange?.(newOpen);
      },
      [isControlled, onOpenChange]
    );

    return (
      <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent
          className={cn(
            className,
            maxHeight,
            'overflow-y-auto pt-0',
            '[&_[data-slot=dropdown-menu-sub-trigger]_svg]:!text-foreground-muted'
          )}
          align={align}
          onCloseAutoFocus={onCloseAutoFocus}
        >
          <SearchableContent
            groups={groups}
            searchPlaceholder={searchPlaceholder}
            emptyMessage={emptyMessage}
            emptyState={emptyState}
            maxHeight={maxHeight}
            onClose={() => handleOpenChange(false)}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
);

SearchableDropdownMenu.displayName = 'SearchableDropdownMenu';
